import * as Sentry from "@sentry/react";
import type { UserResponse } from "@/app/api/types";

/**
 * Set Sentry user and tenant context for distributed tracing.
 * This allows filtering errors and traces by company/user in Sentry dashboard.
 *
 * Tag names mirror the reference implementation in python-ai (`stk_sentry`)
 * and the V2 API (`@stockline/sentry`): `tenant_id` (= company_id) and
 * `tenant_name`. On AWS `environment` is shared (production/staging) across
 * every tenant, so `tenant_id` / `tenant_name` are the only way to isolate a
 * single tenant (e.g. A La Rocca) in Sentry across all three services.
 *
 * `companyName` is passed separately (not read off `user`) because the user
 * payload carries no company name — the caller resolves it from the trusted
 * company query and MUST NOT pass a stale/cross-tenant name, or events would
 * be mis-attributed (Constitution Article III — multi-tenant isolation).
 */
export function setSentryUserContext(
  user: UserResponse | null | undefined,
  companyName?: string | null,
) {
  if (user) {
    // Set user context
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.full_name || user.email,
    });

    // Set company/tenant id tags for filtering — or CLEAR them when the
    // authenticated user has no company. A user just removed from company A
    // (refreshed with company_id: null), or a company-less platform
    // superadmin, must not keep A's company_id/tenant_id tags on their
    // events; a stale id is worse than none (Constitution Article III).
    if (user.company_id) {
      Sentry.setTag("company_id", user.company_id);
      Sentry.setTag("tenant_id", user.company_id);
    } else {
      Sentry.setTag("company_id", undefined);
      Sentry.setTag("tenant_id", undefined);
    }
    // Human-readable tenant name. Set it when a trusted name is resolved,
    // otherwise CLEAR it — a stale name is worse than none. On an in-app
    // switch from tenant A to tenant B, B's company query may not have
    // resolved yet (or its ["company","me"] hydrate is still A's, so the
    // caller passes no name): leaving A's tenant_name would tag B's events
    // with A's name (Constitution Article III). On first login there is no
    // prior name so this is a no-op, and the effect re-fires to enrich the
    // tag once the company query resolves.
    Sentry.setTag("tenant_name", companyName || undefined);

    // Set tenant context for additional info
    Sentry.setContext("tenant", {
      company_id: user.company_id,
      company_name: companyName ?? null,
      user_permissions: user.company_permissions,
    });
  } else {
    // Clear context on logout — tags AND the tenant context object, so a
    // post-logout / unauthenticated event carries no stale tenant metadata.
    // Setting a tag to `undefined` removes it (Sentry's `setTag` accepts a
    // `Primitive`, which includes `undefined`).
    Sentry.setUser(null);
    Sentry.setTag("company_id", undefined);
    Sentry.setTag("tenant_id", undefined);
    Sentry.setTag("tenant_name", undefined);
    Sentry.setContext("tenant", null);
  }
}
